import {
  CLOUD_COUNT,
  STAR_COUNT,
  STREAK_COUNT,
  STREAK_DAY_RGB,
} from '../constants';
import { clamp01, hash, hexToRgb, mixRgb, rgbString } from '../math';
import { bakeCloudPuff, PUFF_HEIGHT, PUFF_WIDTH } from '../sprites';

interface Cloud {
  x: number;
  y: number;
  scale: number;
  speed: number;
}

interface Streak {
  x: number;
  y: number;
  speed: number;
  alpha: number;
  width: number;
  length: number;
}

interface Star {
  x: number;
  y: number;
  size: number;
  twinklePhase: number;
}

export interface SkyFrame {
  width: number;
  height: number;
  /** Still-water line, px. The sky occupies everything above it. */
  baseY: number;
  /** Daylight, 0.05 (night) to 1 (day). */
  daylight: number;
  /** Eased cloud cover, 0 to 1. */
  cloud: number;
  /** Eased storm level, 0 to 1. */
  storm: number;
  /** Wind ramp, 0 (calm) to 1 (strong). Drives cloud and streak speed. */
  windRamp: number;
  dt: number;
  /** Wall-clock seconds, for the star twinkle. */
  timeS: number;
  reducedMotion: boolean;
}

/**
 * Everything above the waterline: the sky gradient, stars, drifting clouds,
 * lightning and the wind streaks.
 *
 * The gradient is cached against a quantised (daylight, cloud, storm) key and
 * the cloud puff is baked to a sprite per storm level, so a steady scene costs
 * one `fillRect` plus a handful of `drawImage` calls.
 */
export class SkyRenderer {
  private gradient: CanvasGradient | null = null;

  private gradientKey = -1;

  private cloudSprite: HTMLCanvasElement | null = null;

  private cloudSpriteKey = -1;

  private readonly clouds: Cloud[] = Array.from(
    { length: CLOUD_COUNT },
    () => ({
      x: Math.random(),
      y: 0.05 + Math.random() * 0.26,
      scale: 0.7 + Math.random() * 0.8,
      speed: 0.25 + Math.random() * 0.5,
    }),
  );

  private readonly streaks: Streak[] = Array.from(
    { length: STREAK_COUNT },
    () => ({
      x: Math.random(),
      y: 0.05 + Math.random() * 0.82,
      speed: 0.6 + Math.random() * 0.9,
      alpha: 0.09 + Math.random() * 0.1,
      width: 0.9 + Math.random() * 0.9,
      length: 0.7 + Math.random() * 0.8,
    }),
  );

  /** A fixed, hash-positioned star field, so the night sky never reshuffles. */
  private readonly stars: Star[] = Array.from(
    { length: STAR_COUNT },
    (_, i) => ({
      x: hash(i * 1.71),
      y: 0.03 + hash(i * 3.37) * 0.8,
      size: 0.6 + hash(i * 5.13) * 1.1,
      twinklePhase: hash(i * 7.77) * 6.28,
    }),
  );

  private flash = 0;

  private timeToNextFlash = 2;

  private boltX = 0.5;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  /** Size-dependent caches must rebuild after a resize. */
  invalidate(): void {
    this.gradientKey = -1;
  }

  draw(frame: SkyFrame): void {
    this.drawGradient(frame);
    this.drawStars(frame);
    this.drawClouds(frame);
    this.drawLightning(frame);
    this.drawStreaks(frame);
  }

  /**
   * Clear day/night gradient, tinted toward overcast by the cloud amount and
   * darkened by the storm level.
   */
  private drawGradient({
    width,
    height,
    baseY,
    daylight,
    cloud,
    storm,
  }: SkyFrame): void {
    const { ctx } = this;

    const topClear = mixRgb(hexToRgb('13202c'), hexToRgb('EAF6FD'), daylight);
    const bottomClear = mixRgb(
      hexToRgb('1b2f40'),
      hexToRgb('CDEBFB'),
      daylight,
    );
    const topOvercast = mixRgb(
      hexToRgb('243038'),
      hexToRgb('C6CFD7'),
      daylight,
    );
    const bottomOvercast = mixRgb(
      hexToRgb('2a363f'),
      hexToRgb('AFBAC4'),
      daylight,
    );

    let top = mixRgb(topClear, topOvercast, cloud);
    let bottom = mixRgb(bottomClear, bottomOvercast, cloud);

    if (storm > 0) {
      top = mixRgb(top, hexToRgb('1a2128'), storm * 0.45);
      bottom = mixRgb(bottom, hexToRgb('20272e'), storm * 0.45);
    }

    // Quantise the three drivers to 49 steps each and pack them into one
    // number. Each term is at most 48, so the 64-wide bands never collide and
    // the key changes only when a driver crosses a step, which is what keeps
    // the gradient out of the per-frame path.
    const key =
      Math.round(daylight * 48) * 4096 +
      Math.round(cloud * 48) * 64 +
      Math.round(storm * 48);

    if (key !== this.gradientKey) {
      this.gradient = ctx.createLinearGradient(0, 0, 0, baseY);
      this.gradient.addColorStop(0, rgbString(top));
      this.gradient.addColorStop(1, rgbString(bottom));
      this.gradientKey = key;
    }

    if (this.gradient != null) {
      ctx.fillStyle = this.gradient;
      ctx.fillRect(0, 0, width, height);
    }
  }

  /** Stars fade in as the light drops, hide behind heavy cloud and twinkle gently. */
  private drawStars({
    width,
    baseY,
    daylight,
    cloud,
    timeS,
    reducedMotion,
  }: SkyFrame): void {
    const nightAmount = clamp01((0.35 - daylight) / 0.35) * (1 - cloud * 0.85);

    if (nightAmount <= 0.02) {
      return;
    }

    const { ctx } = this;
    ctx.fillStyle = '#EAF2FA';

    this.stars.forEach((star) => {
      const twinkle = reducedMotion
        ? 0.75
        : 0.55 + 0.45 * Math.sin(timeS * 1.4 + star.twinklePhase);

      ctx.globalAlpha = nightAmount * twinkle * 0.85;
      ctx.fillRect(star.x * width, star.y * baseY, star.size, star.size);
    });

    ctx.globalAlpha = 1;
  }

  /**
   * Soft radial-gradient puffs. Each disc fades to transparent so overlaps blend
   * with no hard edges — no facets or triangles. Opacity follows the cloud
   * amount, dimmer at night and greyer in a storm.
   */
  private drawClouds({
    width,
    baseY,
    daylight,
    cloud,
    storm,
    windRamp,
    dt,
    reducedMotion,
  }: SkyFrame): void {
    if (cloud <= 0.02) {
      return;
    }

    const { ctx } = this;

    // The sprite tint tracks the storm level in eighths; alpha carries cover and
    // daylight, so one bake covers a wide range of conditions.
    const stormStep = Math.round(storm * 8);

    if (stormStep !== this.cloudSpriteKey) {
      this.cloudSprite = bakeCloudPuff(stormStep / 8);
      this.cloudSpriteKey = stormStep;
    }

    if (this.cloudSprite == null) {
      return;
    }

    ctx.globalAlpha = cloud * 0.45 * (0.5 + 0.5 * daylight);

    this.clouds.forEach((c) => {
      if (!reducedMotion) {
        // eslint-disable-next-line no-param-reassign
        c.x -= ((5 + 26 * windRamp) * c.speed * dt) / Math.max(width, 1);
        // eslint-disable-next-line no-param-reassign
        c.x = ((c.x % 1) + 1) % 1;
      }

      const x = c.x * (width + 160) - 80;
      const y = c.y * baseY;

      ctx.drawImage(
        this.cloudSprite as HTMLCanvasElement,
        x - (PUFF_WIDTH / 2) * c.scale,
        y - (PUFF_HEIGHT / 2) * c.scale,
        PUFF_WIDTH * c.scale,
        PUFF_HEIGHT * c.scale,
      );
    });

    ctx.globalAlpha = 1;
  }

  /** In a storm, fire an occasional brief flash plus a faint bolt across the sky. */
  private drawLightning({
    width,
    baseY,
    storm,
    dt,
    reducedMotion,
  }: SkyFrame): void {
    const { ctx } = this;

    if (storm > 0.35 && !reducedMotion) {
      this.timeToNextFlash -= dt;

      if (this.timeToNextFlash <= 0) {
        this.flash = 1;
        this.boltX = 0.18 + Math.random() * 0.64;
        this.timeToNextFlash = 1.8 + (Math.random() * 4.5) / storm;
      }

      this.flash = Math.max(0, this.flash - dt * 3.4);
    } else {
      this.flash = 0;
    }

    if (this.flash <= 0.01) {
      return;
    }

    ctx.fillStyle = `rgba(216,228,244,${(this.flash * 0.32 * storm).toFixed(3)})`;
    ctx.fillRect(0, 0, width, baseY);

    if (this.flash > 0.55) {
      ctx.strokeStyle = `rgba(238,244,255,${(this.flash * 0.5).toFixed(3)})`;
      ctx.lineWidth = 1.6;

      let x = this.boltX * width;
      let y = 4;

      ctx.beginPath();
      ctx.moveTo(x, y);

      for (let k = 0; k < 5; k += 1) {
        y += baseY * 0.18;
        x += (Math.random() - 0.5) * 26;
        ctx.lineTo(x, y);
      }

      ctx.stroke();
    }
  }

  /**
   * Atmosphere: thin lines drifting left (downwind). Density, speed and length
   * all grow with the wind, so a calm sky is empty.
   */
  private drawStreaks({
    width,
    baseY,
    daylight,
    windRamp,
    dt,
    reducedMotion,
  }: SkyFrame): void {
    if (windRamp <= 0.002) {
      return;
    }

    const { ctx } = this;

    const r = Math.round(255 + (STREAK_DAY_RGB[0] - 255) * daylight);
    const g = Math.round(255 + (STREAK_DAY_RGB[1] - 255) * daylight);
    const b = Math.round(255 + (STREAK_DAY_RGB[2] - 255) * daylight);

    this.streaks.forEach((streak) => {
      const length = (14 + 50 * windRamp) * streak.length;

      if (!reducedMotion) {
        // eslint-disable-next-line no-param-reassign
        streak.x -=
          ((20 + 175 * windRamp) * streak.speed * dt) / Math.max(width, 1);
        // eslint-disable-next-line no-param-reassign
        streak.x = ((streak.x % 1) + 1) % 1;
      }

      const x = streak.x * (width + length) - length;
      const y = streak.y * baseY * 0.96 + baseY * 0.02;
      const alpha = streak.alpha * windRamp * (0.7 + 0.45 * daylight);

      ctx.strokeStyle = `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
      ctx.lineWidth = streak.width;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + length, y + length * 0.04);
      ctx.stroke();
    });
  }
}
